import { Box, Container, Divider, Grid } from "@mui/material";
import { useSlugValidation } from "@platformx/authoring-apis";
import {
  CanonicalFieldInput,
  CanonicalFieldLabel,
  frameDocumentType,
  getBrandNameById,
  getCanonicalPageUrl,
  toAbsoluteUrl,
  getTitleMax,
  SchedulePublishUnpublish,
  SectionWrapper,
  ShowToastError,
  ShowToastSuccess,
  SlugFieldInput,
  SlugFieldLabel,
  useAccess,
} from "@platformx/utilities";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CONTENT_STATUS_PERMISSION, NOTIFICATION_PERMISSION } from "../../utils/constants";
import { SectionProps } from "../../utils/types";
import { CreateHeader } from "../common/CreateHeader/CreateHeader";
import DynamicSectionComponent from "../DynamicSectionComponent";
import FormGroupPageScroll from "../FormGroupPageScroll/FormGroupPageScroll";
import { useCustomStyle } from "./DynamicForm.style";

const SCROLLABLE_CONTAINER_ID = "dynamicFormScrollableDiv";
const formGroupSectionId = (index: number | string) => `formGroup_${index}`;

// The title field's maxLength must match the site-wide 255-char title limit
// (see @platformx/utilities getTitleMax) regardless of what the backend schema
// declares for it — dynamic/schema-based content types are not excluded.
const applyTitleFieldMaxLength = (fields: any[] = []) => {
  const max = getTitleMax();
  return fields?.map((field) => {
    if (field?.name?.toLowerCase() !== "title") return field;
    const message = `${max} characters max`;
    const hasMaxLength = field?.validations?.some((v: any) => v.type === "maxLength");
    const validations = hasMaxLength
      ? field.validations.map((v: any) =>
          v.type === "maxLength" ? { ...v, value: max, message } : v,
        )
      : [...(field?.validations || []), { type: "maxLength", value: max, message }];
    return { ...field, validations };
  });
};

export const DynamicComponent = ({
  contentType,
  template,
  handleSave,
  handlePublish,
  contentInstance,
  setContentInstance,
  isEdit,
  type,
  // setPageState,
  listUrl,
  // KIWI-6576: optional. The schedule lives OUTSIDE contentInstance on purpose — the save mutations
  // validate every key against the schema and reject unknown ones. Without these two props the
  // Schedule section simply is not rendered, so existing callers are unaffected.
  schedule,
  setSchedule,
  scheduleErrors,
  schedulePending,
}) => {
  const { t, i18n } = useTranslation();
  const classes = useCustomStyle();
  const navigate = useNavigate();
  const slugValidation = useSlugValidation(contentType);
  const originalSlugRef = useRef<string | null>(null);
  const { canAccessAction } = useAccess();
  /* Notification/Content Status sections are driven by the logged-in user's session permissions, same as VerticalTabsArticle */
  const hasNotificationAccess = canAccessAction(
    NOTIFICATION_PERMISSION.category,
    NOTIFICATION_PERMISSION.subCategory,
    NOTIFICATION_PERMISSION.action,
  );
  const hasContentStatusAccess = canAccessAction(
    CONTENT_STATUS_PERMISSION.category,
    CONTENT_STATUS_PERMISSION.subCategory,
    CONTENT_STATUS_PERMISSION.action,
  );

  const visibleFields =
    applyTitleFieldMaxLength(template?.fields)?.filter((field) => field?.is_show !== false) || [];

  // Group only visible fields based on index values. Groups are collected in
  // whichever order their fields first appear in template.fields, which doesn't
  // necessarily match numeric form_groups order (e.g. a schema edited/reordered
  // over time can list a group-5 field before a group-4 field) — sort by index
  // afterward so sections always render 01, 02, 03... regardless of field order.
  const groupedFields = visibleFields
    ?.reduce((result, field) => {
      const { index, ...rest } = field;
      const existingGroup = result?.find((group) => group.index === index);
      if (existingGroup) {
        existingGroup.fields.push(rest);
      } else {
        result.push({
          index,
          title: template?.form_groups?.find((x) => x.index === index)?.title,
          description: template?.form_groups?.find((x) => x.index === index)?.description,
          fields: [rest],
        });
      }
      return result;
    }, [])
    ?.sort((a, b) => Number(a.index) - Number(b.index))
    ?.filter((group) => {
      if (!hasNotificationAccess && /^notification$/i.test(group?.title || "")) return false;
      if (!hasContentStatusAccess && /^content status$/i.test(group?.title || "")) return false;
      return true;
    });

  const seoSectionIndex = groupedFields?.findIndex((group) => /^seo$/i.test(group?.title || ""));

  // Some schemas (e.g. Profile) declare a slug-named field outside the SEO
  // section (e.g. under "Title & Description") — drop it there so slug is only
  // ever edited once, from the SEO section. Only do this when an SEO section
  // actually exists; otherwise there'd be nowhere left for the field to live,
  // silently making that content type's slug unreachable.
  if (seoSectionIndex >= 0) {
    groupedFields?.forEach((group) => {
      if (/^seo$/i.test(group?.title || "")) return;
      group.fields = group.fields?.filter((field) => !/^slug$/i.test(field?.name || ""));
    });
  }

  // Some schemas (e.g. Profile) already declare their own slug-named field inside
  // the SEO section — in that case leave the section entirely schema-driven and
  // don't inject a duplicate.
  const seoSectionHasOwnSlugField = groupedFields?.[seoSectionIndex]?.fields?.some((field) =>
    /^slug$/i.test(field?.name || ""),
  );
  const showGeneratedSlugField = seoSectionIndex >= 0 && !seoSectionHasOwnSlugField;
  const isCollection = template?.schema_document_type === "collection";

  // The Schedule section is not part of any schema's form_groups, so it is appended after them (and
  // numbered after the last one) rather than requiring every tenant's schemas to be re-registered.
  // Scheduling is for content types only: a collection has no public page to take live or down.
  const showScheduleSection = Boolean(schedule && setSchedule) && !isCollection;
  const scheduleSectionId = formGroupSectionId("schedule");
  const scheduleSectionNumber = String(
    Math.max(0, ...(groupedFields ?? []).map((group) => Number(group.index) || 0)) + 1,
  ).padStart(2, "0");
  // One list drives both the nav rail and the scroll-spy so the Schedule section behaves like any other.
  const sectionIds = [
    ...(groupedFields ?? []).map((group) => formGroupSectionId(group.index)),
    ...(showScheduleSection ? [scheduleSectionId] : []),
  ];

  // Scroll-spy for the form_group nav rail, scoped to this form's own
  // scrollable container.
  const [activeFormGroupId, setActiveFormGroupId] = useState("");
  const scrollDebounceRef = useRef<any>(null);
  // How far below the container's top a section's own top edge may sit and
  // still count as "current" — a section only barely peeking in at the very
  // top (a few leftover pixels of the previous one still visible above it)
  // should not itself become active yet.
  const SCROLL_SPY_TOP_THRESHOLD = 24;

  useEffect(() => {
    if (!sectionIds.length) return undefined;

    const scrollHandler = () => {
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }
      scrollDebounceRef.current = setTimeout(() => {
        const container = document.getElementById(SCROLLABLE_CONTAINER_ID);
        if (!container) return;
        const containerTop = container.getBoundingClientRect().top;

        // Sections are listed top-to-bottom (groupedFields is sorted by index) —
        // the active one is the LAST section whose top has already scrolled up
        // to (or past) the threshold, not just any section with SOME overlap
        // (that picks whichever section comes first even if it's almost
        // entirely scrolled out already, e.g. section 12 while 13 fills the
        // view — a stray sliver of 12 still overlapping the very top).
        const [firstId] = sectionIds;
        let active = firstId;
        for (const id of sectionIds) {
          const element = document.getElementById(id);
          if (!element) continue;
          const relativeTop = element.getBoundingClientRect().top - containerTop;
          if (relativeTop <= SCROLL_SPY_TOP_THRESHOLD) {
            active = id;
          } else {
            break;
          }
        }
        setActiveFormGroupId(active);
      }, 100);
    };

    setActiveFormGroupId(sectionIds[0]);
    const container = document.getElementById(SCROLLABLE_CONTAINER_ID);
    container?.addEventListener("scroll", scrollHandler);
    return () => {
      container?.removeEventListener("scroll", scrollHandler);
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIds.length]);

  useEffect(() => {
    if (slugValidation.isSubmitCheck) return;
    if (slugValidation.status === "unique") {
      ShowToastSuccess(t("slug_available", { docType: template?.title }));
    } else if (slugValidation.status === "not_unique") {
      ShowToastError(t("slug_not_unique", { docType: template?.title }));
    }
  }, [slugValidation.status, slugValidation.isSubmitCheck]);

  const handleSlugChange = (event) => {
    const { value } = event.target;
    setContentInstance((prev) => ({ ...prev, slug: value }));
    slugValidation.invalidate();
  };

  const handleSlugBlur = () => {
    slugValidation.validate(contentInstance?.slug, {
      excludeValue: originalSlugRef.current || "",
      schema_document_type: frameDocumentType(template?.schema_document_type || "content"),
    });
  };

  // Capture the fetched slug once, on mount — never overwritten again, so later
  // edits still get compared against the original stored value. Gated on
  // hasCapturedOriginalSlug (not "is contentInstance.slug truthy"): a legacy
  // document can have a genuinely empty stored slug, and by the time this
  // component mounts in edit mode contentInstance is already the real fetched
  // data (DynamicComponentContainer only renders it once loading finishes) —
  // so the empty string is itself a valid baseline to capture immediately.
  // Waiting for the first non-empty value would instead capture whatever the
  // user's first edit produces; if that edit arrives as a single paste (not
  // incremental typing), it would wrongly become its own "original" and
  // silently skip the uniqueness check for a brand-new, colliding slug.
  // Still gated on isEdit: in create mode there is no stored value at all, so
  // capturing on mount there would do the same thing for a typed-from-scratch
  // slug.
  const hasCapturedOriginalSlug = useRef(false);
  useEffect(() => {
    if (isEdit && !hasCapturedOriginalSlug.current) {
      hasCapturedOriginalSlug.current = true;
      originalSlugRef.current = contentInstance?.slug || "";
    }
  }, [isEdit, contentInstance?.slug]);

  const currentSlug = contentInstance?.slug || "";
  const slugField = {
    value: currentSlug,
    onChange: handleSlugChange,
    onBlur: handleSlugBlur,
    // The stored canonical is the source of truth: shown as-is until the slug changes, then
    // rebuilt from the document's own primary brand.
    canonicalUrl:
      currentSlug && !isCollection
        ? contentInstance?.canonical_url && currentSlug === originalSlugRef.current
          ? toAbsoluteUrl(contentInstance.canonical_url)
          : getCanonicalPageUrl(
              currentSlug,
              i18n.language,
              contentType,
              getBrandNameById(contentInstance?.brand_name || ""),
            )
        : "",
  };

  const checkSlugThenRun = async (run: () => void) => {
    // Only content types that actually show a slug field (an SEO section
    // exists — generated or schema-declared) require a value; other content
    // types never had a slug field to fill in, so currentSlug staying empty
    // for them is normal, not a cleared-out required field.
    if (seoSectionIndex >= 0 && !currentSlug) {
      ShowToastError(t("slug_required"));
      return;
    }
    if (currentSlug) {
      const isUnique = await slugValidation.checkNow(currentSlug, {
        excludeValue: originalSlugRef.current || "",
        schema_document_type: frameDocumentType(template?.schema_document_type || "content"),
      });
      if (!isUnique) {
        ShowToastError(t("slug_url_not_unique", { docType: template?.title }));
        return;
      }
      // The value just cleared uniqueness — advance the baseline now so a later
      // save/publish in this same session (e.g. Publish right after Save as
      // Draft) excludes against the value the document now actually owns,
      // instead of re-checking it against itself and failing as "not unique".
      // Refs aren't subject to the read/write races this rule guards against;
      // currentSlug was already captured (as the excludeValue check above)
      // before this await.
      // eslint-disable-next-line require-atomic-updates
      originalSlugRef.current = currentSlug;
    }
    run();
  };

  //ReactHookForm setup
  const form = useForm({
    defaultValues: contentInstance,
    mode: "onBlur",
  });

  const { register, formState, clearErrors, handleSubmit, control } = form;
  const { errors } = formState;

  const onError = (errorss, e) => {
    //function to focus errors on custom elements like image picker, tags
    e.preventDefault();
    if (Object.keys(errorss).length > 0) {
      const goToError = document.getElementById(`${Object.keys(errorss)[0]}_id`);
      if (goToError) goToError.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };
  const guardedPublish = (data, e) => checkSlugThenRun(() => handlePublish(data, e));
  return (
    <form onSubmit={handleSubmit(guardedPublish, onError)}>
      <Box>
        <CreateHeader
          createText={
            isEdit ? `${t("edit")} ${template?.title}` : `${t("create")} ${template?.title}`
          }
          publishText={t("publish")}
          saveText={t("save_as_draft")}
          previewText={t("preview")}
          toolTipText={t("preview_tooltip")}
          saveVariant='outlined'
          category={type}
          subCategory={contentType}
          id={""}
          hasPreviewButton={false}
          handleReturn={() => {
            navigate(`/${listUrl}/${contentType}`);
          }}
          handlePublish={() => {
            // setPageState("publish");
          }}
          handleSaveOrPublish={() => {
            // setPageState("save");
            checkSlugThenRun(() => handleSave("update"));
          }}
          editText={""}
          isQuiz={false}
          hasPublishButton={false}
          hasSaveButton={false}
          showPreview={false}
          workflow={{ enable: false }}
          hasTimerState={false}
          lastModifiedDate={""}
          isFeatured={false}
          setIsFeatured={() => {}}
        />
        <Divider></Divider>
      </Box>
      <Box
        id={SCROLLABLE_CONTAINER_ID}
        sx={{
          position: "relative",
          height: { md: "calc(-126px + 100vh)", xs: "calc(-45px + 100vh)" },
          overflowY: "auto",
        }}>
        {sectionIds.length > 1 && (
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              transform: "translateY(-50%)",
              right: { sm: "5px", xs: 0 },
              zIndex: 1000,
            }}>
            <FormGroupPageScroll
              groups={[
                ...groupedFields.map((group) => ({
                  id: formGroupSectionId(group.index),
                  title: group.title,
                })),
                ...(showScheduleSection
                  ? [{ id: scheduleSectionId, title: t("schedule_publish_unpublish_title") }]
                  : []),
              ]}
              activeId={activeFormGroupId}
            />
          </Box>
        )}
        {groupedFields?.length > 0 &&
          groupedFields.map((section: SectionProps, index: number) => {
            return (
              <Box
                id={formGroupSectionId(section?.index)}
                className={classes.mainStyleWrapper}
                key={section?.index}>
                <SectionWrapper
                  number={section.index}
                  title={section.title}
                  subTitle={section.description}
                  titleVariant={"h5bold"}
                  subTitleVariant={"p4regular"}>
                  <DynamicSectionComponent
                    fields={section.fields}
                    state={contentInstance}
                    setState={setContentInstance}
                    errors={errors}
                    register={register}
                    clearErrors={clearErrors}
                    isEdit={isEdit}
                    control={control}
                    title={template?.title}
                    category={type}
                  />
                  {showGeneratedSlugField && index === seoSectionIndex && (
                    <Container>
                      <Grid container spacing={5} rowSpacing={2}>
                        <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiled'>
                          <SlugFieldLabel />
                        </Grid>
                        <Grid item xs={12} sm={7} md={7} lg={7} className='textFiled'>
                          <SlugFieldInput
                            value={slugField.value}
                            onChange={slugField.onChange}
                            onBlur={slugField.onBlur}
                          />
                        </Grid>
                        {slugField.canonicalUrl && (
                          <>
                            <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiled'>
                              <CanonicalFieldLabel />
                            </Grid>
                            <Grid item xs={12} sm={7} md={7} lg={7} className='textFiled'>
                              <CanonicalFieldInput canonicalUrl={slugField.canonicalUrl} />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Container>
                  )}
                </SectionWrapper>
              </Box>
            );
          })}
        {showScheduleSection && (
          <Box id={scheduleSectionId} className={classes.mainStyleWrapper}>
            <SchedulePublishUnpublish
              variant='section'
              sectionNumber={scheduleSectionNumber}
              value={schedule}
              onChange={setSchedule}
              errors={scheduleErrors}
              pending={schedulePending}
            />
          </Box>
        )}
      </Box>
    </form>
  );
};
